import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import InquiryForms from "@/components/InquiryForms";
import { COMPANY, HERO_IMAGE } from "@/lib/site-data";
import { submitLead } from "@/lib/api";

function GeneralContactForm() {
    const initial = { name: "", company: "", designation: "", email: "", mobile: "", service_requirement: "", monthly_dispatch_volume: "", message: "" };
    const [data, setData] = useState(initial);
    const [loading, setLoading] = useState(false);
    const onChange = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead("contact", data);
            toast.success("Message received. We'll respond within 24 hours.");
            setData(initial);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form data-testid="general-contact-form" onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><label className="label-vl">Name *</label><input data-testid="gc-name" name="name" required value={data.name} onChange={onChange} className="input-vl" /></div>
            <div><label className="label-vl">Company *</label><input data-testid="gc-company" name="company" required value={data.company} onChange={onChange} className="input-vl" /></div>
            <div><label className="label-vl">Designation</label><input data-testid="gc-designation" name="designation" value={data.designation} onChange={onChange} className="input-vl" /></div>
            <div><label className="label-vl">Email *</label><input data-testid="gc-email" type="email" name="email" required value={data.email} onChange={onChange} className="input-vl" /></div>
            <div><label className="label-vl">Mobile *</label><input data-testid="gc-mobile" name="mobile" required value={data.mobile} onChange={onChange} className="input-vl" /></div>
            <div>
                <label className="label-vl">Service Requirement</label>
                <select data-testid="gc-service" name="service_requirement" value={data.service_requirement} onChange={onChange} className="input-vl">
                    <option value="">Select…</option>
                    {["Transportation", "Warehousing", "3PL", "4PL", "Contract Logistics", "Reverse Logistics", "Supply Chain Consulting"].map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
            </div>
            <div className="md:col-span-2">
                <label className="label-vl">Monthly Dispatch Volume</label>
                <input data-testid="gc-volume" name="monthly_dispatch_volume" value={data.monthly_dispatch_volume} onChange={onChange} placeholder="e.g. 200 MT or 25 trips" className="input-vl" />
            </div>
            <div className="md:col-span-2">
                <label className="label-vl">Message</label>
                <textarea data-testid="gc-message" name="message" rows={4} value={data.message} onChange={onChange} className="input-vl" />
            </div>
            <div className="md:col-span-2">
                <button type="submit" data-testid="gc-submit" disabled={loading} className="btn-primary disabled:opacity-60">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {loading ? "Sending…" : "Send Message"}
                </button>
            </div>
        </form>
    );
}

export default function Contact() {
    return (
        <div data-testid="page-contact">
            <PageHeader
                overline="Contact"
                title="Let's design your next supply chain."
                subtitle="Speak to a logistics expert directly — or use a specialised inquiry form below to get a precise proposal."
                image={HERO_IMAGE}
                breadcrumb={[{ label: "Contact" }]}
                testId="contact-header"
            />

            {/* Direct contact info */}
            <section className="bg-white py-16 md:py-20" data-testid="contact-info">
                <div className="container-vl grid grid-cols-1 md:grid-cols-3 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
                    <a href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`} className="bg-white p-7 hover:bg-[#28406A] hover:text-white transition-colors group" data-testid="contact-phone">
                        <Phone className="h-8 w-8 text-[#F4A000] mb-4" strokeWidth={1.5} />
                        <p className="overline mb-1 group-hover:text-[#F4A000]">Call us</p>
                        <p className="font-display uppercase text-xl">{COMPANY.phones[0]}</p>
                        <p className="text-sm text-[#4B6390] group-hover:text-[#CBD5E1]">{COMPANY.phones[1]}</p>
                    </a>
                    <a href={`mailto:${COMPANY.email}`} className="bg-white p-7 hover:bg-[#28406A] hover:text-white transition-colors group" data-testid="contact-email">
                        <Mail className="h-8 w-8 text-[#F4A000] mb-4" strokeWidth={1.5} />
                        <p className="overline mb-1 group-hover:text-[#F4A000]">Email</p>
                        <p className="font-display uppercase text-base break-all">{COMPANY.email}</p>
                    </a>
                    <div className="bg-white p-7 hover:bg-[#28406A] hover:text-white transition-colors group" data-testid="contact-address">
                        <MapPin className="h-8 w-8 text-[#F4A000] mb-4" strokeWidth={1.5} />
                        <p className="overline mb-1 group-hover:text-[#F4A000]">Head office</p>
                        <p className="font-display uppercase text-base leading-tight">{COMPANY.address}</p>
                    </div>
                </div>
            </section>

            {/* Specialised inquiry tabs */}
            <section className="bg-[#E9EEF3] py-20" data-testid="contact-tabs-section">
                <div className="container-vl">
                    <p className="overline mb-3">Specialised Inquiries</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-10 max-w-3xl">Pick the inquiry type that matches your need.</h2>
                    <InquiryForms defaultTab="consultation" testId="contact-inquiry-forms" />
                </div>
            </section>

            {/* General contact form */}
            <section className="bg-white py-20" data-testid="contact-general-section">
                <div className="container-vl grid grid-cols-12 gap-10">
                    <div className="col-span-12 lg:col-span-4">
                        <p className="overline mb-3">General Message</p>
                        <h2 className="heading-h2 text-[#101A2C] mb-5">Just want to say hello?</h2>
                        <p className="body-lg">Drop us a general message and someone from our team will respond within 24 hours.</p>
                    </div>
                    <div className="col-span-12 lg:col-span-8">
                        <GeneralContactForm />
                    </div>
                </div>
            </section>

            {/* Map */}
            <section className="bg-[#101A2C]" data-testid="contact-map">
                <iframe
                    title="Vibhuti Logistics — Waluj MIDC, Aurangabad"
                    src="https://www.google.com/maps?q=Waluj+MIDC+Aurangabad&output=embed"
                    width="100%"
                    height="450"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                />
            </section>
        </div>
    );
}
